import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getUpdatedBasket } from "@/services/api";
import {
  BasketSummaryResponse,
  BasketSummaryResponseSimType,
} from "@/services/model";
import {
  getFlatHomeBaseDisplayValue,
  getSelectedOptionForBasketProduct,
  isFlatHomeTariff,
} from "@/utils/tariffUtils";
import { getWizardRouteDecision, WizardEntry } from "@/utils/wizardNavigation";

/**
 * Hook to activate a purchase intent and redirect the user to the appropriate checkout step.
 */
export const useActivatePurchaseIntent = () => {
  const navigate = useNavigate();
  const { setDataContext } = useContext(DataContext);
  const [isActivating, setIsActivating] = useState(false);

  /**
   * Activates the given purchase intent.
   *
   * @param intent - The purchase intent to activate.
   */
  const activateIntent = async (intent: BasketSummaryResponse) => {
    if (!intent.basketId) {
      return;
    }

    setIsActivating(true);

    try {
      // Fetch the basket data in its initial form
      const basket = await getUpdatedBasket(intent.basketId);

      const optionProductId =
        intent.productId ?? basket.entries?.["0"]?.options?.[0]?.product?.id;
      let selectedOption =
        basket.id && optionProductId
          ? await getSelectedOptionForBasketProduct(basket.id, optionProductId)
          : undefined;

      if (
        !selectedOption &&
        optionProductId &&
        isFlatHomeTariff(optionProductId)
      ) {
        const baseDisplayValue = getFlatHomeBaseDisplayValue(optionProductId);

        if (baseDisplayValue) {
          selectedOption = {
            id: optionProductId,
            baseDisplayValue,
            isVoiceProduct: false,
          };
        }
      }

      const isTripleSim =
        intent.simType === BasketSummaryResponseSimType.TRIPLESIM;
      const isEsim = intent.simType === BasketSummaryResponseSimType.ESIM;
      const { includesNumberPorting } = getWizardRouteDecision(optionProductId);

      let allowedStep: number | undefined;
      let targetRoute: string | undefined;
      let wizardEntry: WizardEntry | undefined;

      if (isTripleSim) {
        allowedStep = 1;
        targetRoute = portalRoutes.addDevice.simSelection;
        wizardEntry = {
          source: "purchase-intent",
          lockedSimType: "physical",
          optionId: optionProductId,
        };
      } else if (isEsim) {
        wizardEntry = {
          source: "purchase-intent",
          lockedSimType: "esim",
          optionId: optionProductId,
        };

        if (includesNumberPorting) {
          allowedStep = 3;
          targetRoute = portalRoutes.addDevice.numberPorting;
        } else {
          allowedStep = 4;
          targetRoute = portalRoutes.addDevice.checkout;
        }
      }

      // Reset step-specific context from any previous onboarding/add-device flow
      setDataContext((prev) => ({
        ...prev,
        basket,
        numberPorting: undefined,
        checkout: undefined,
        simVerification: undefined,
        selectedOption,
        wizardEntry,
        ...(allowedStep !== undefined && {
          allowedMaxOnboardingStep: allowedStep,
        }),
      }));

      // Navigate to the appropriate step in the AddDevice checkout.
      // keepBasket: AddDevice clears leftover baskets on mount unless told otherwise.
      if (targetRoute) {
        navigate(targetRoute, { state: { keepBasket: true } });
      }
    } catch (error) {
      console.error("Failed to activate purchase intent:", error);
      throw error;
    } finally {
      setIsActivating(false);
    }
  };

  return { activateIntent, isActivating };
};
